import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, ShieldCheck, Gavel, Truck, Sparkles, Clock3, Users, Package, CheckCircle2, Layers, TrendingDown, Lock,
  Palette, Boxes, Scissors, Target,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inrCompact = (n) => {
  if (!n || n < 1000) return `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
  if (n < 100000) return `₹${(n / 1000).toFixed(1)}k`;
  if (n < 10000000) return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${(n / 10000000).toFixed(1)} Cr`;
};

export default function Home() {
  return (
    <div className="marketing">
      <NavBar />
      <Hero />
      <TrustStrip />
      <AboutSummary />
      <ValueProps />
      <HowItWorks />
      <BuyerProtection />
      <ThreeSided />
      <Numbers />
      <FinalCta />
      <Footer />
    </div>
  );
}

function NavBar() {
  return (
    <header className="m-nav" data-testid="marketing-navbar">
      <Link to="/" className="m-brand" data-testid="brand-home-link">
        <img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/>
      </Link>
      <nav className="m-nav-links">
        <a href="#how" data-testid="nav-how">How it works</a>
        <a href="#protection" data-testid="nav-protection">Buyer protection</a>
        <a href="#network" data-testid="nav-network">Supplier network</a>
        <Link to="/about" data-testid="nav-about">About us</Link>
        <Link to="/contact" data-testid="nav-contact">Contact us</Link>
        <Link to="/partner" data-testid="nav-partner">Partner with us</Link>
      </nav>
      <div className="m-nav-actions">
        <Link to="/login" className="m-ghost" data-testid="nav-sign-in">Sign in</Link>
        <Link to="/register" className="m-cta" data-testid="nav-launch-workspace">
          Get started <ArrowRight size={15}/>
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="m-hero">
      <div className="m-hero-copy">
        <p className="eyebrow" data-testid="hero-eyebrow">SEALED-BID PROCUREMENT · ESCROW-PROTECTED</p>
        <h1 data-testid="hero-title" className="m-hero-h1-long">
          India's first tech-driven B2B marketplace for <span className="m-accent">commercial printing</span> &amp; custom packaging.
        </h1>
        <p className="m-hero-sub" data-testid="hero-sub">
          GoPrintPack.com replaces manual RFQ friction with real-time reverse auctions and secure escrow protection.
        </p>
        <div className="m-hero-cta">
          <Link to="/login" className="m-cta m-cta-lg" data-testid="hero-primary-cta">
            Start a sealed auction <ArrowRight size={17}/>
          </Link>
          <a href="#how" className="m-outline" data-testid="hero-secondary-cta">See how it works</a>
        </div>
        <LiveSupplierBadge />
        <div className="m-hero-badges">
          <Badge icon={ShieldCheck} label="Escrow-protected payments" testid="badge-escrow"/>
          <Badge icon={Lock} label="Anonymous bidding" testid="badge-anonymous"/>
          <Badge icon={TrendingDown} label="Average 24% cost saved" testid="badge-savings"/>
        </div>
      </div>
      <div className="m-hero-visual" data-testid="hero-visual">
        <div className="m-auction-card">
          <div className="m-auction-head">
            <span className="status live">Live auction · 04:12:38 left</span>
            <b>Summer coffee pouch run · 12,000 units</b>
          </div>
          <MockBidRow label="Supplier Alpha" price="₹2,28,500" score="93.2" turn="6 days" rating="4.8 ★" rank="1"/>
          <MockBidRow label="Supplier Beta" price="₹2,35,900" score="92.0" turn="8 days" rating="4.9 ★" rank="2"/>
          <MockBidRow label="Supplier Gamma" price="₹2,43,000" score="89.8" turn="7 days" rating="4.7 ★" rank="3"/>
          <div className="m-auction-foot">
            <ShieldCheck size={13}/> Supplier identities stay masked — awards go purely by score
          </div>
        </div>
        <div className="m-float-card m-float-a">
          <Package size={16}/><div><b>Kraft substrate matched</b><span>3 regional presses notified</span></div>
        </div>
        <div className="m-float-card m-float-b">
          <ShieldCheck size={16}/><div><b>₹2,28,500 held in escrow</b><span>Released on delivery scan</span></div>
        </div>
      </div>
    </section>
  );
}

const Badge = ({ icon: Icon, label, testid }) => (
  <div className="m-badge" data-testid={testid}>
    <Icon size={14}/> {label}
  </div>
);

const MockBidRow = ({ label, price, score, turn, rating, rank }) => (
  <div className="m-bid-row" data-testid={`mock-bid-row-${rank}`}>
    <div className="m-bid-rank">{rank}</div>
    <div className="m-bid-info">
      <b>{label}</b>
      <span>{rating} · {turn} · <em>score {score}</em></span>
    </div>
    <strong>{price}</strong>
  </div>
);

function TrustStrip() {
  return (
    <section className="m-trust" data-testid="trust-strip">
      <span className="eyebrow">TRUSTED BY 340+ BRAND OPERATORS</span>
      <div className="m-trust-row">
        {["Northstar Goods","Vale Botanics","Meridian Coffee","Studio Field","Quarry & Grain","Blueberry Print Co."].map(n =>
          <span key={n} data-testid={`trust-logo-${n.split(" ")[0].toLowerCase()}`}>{n}</span>
        )}
      </div>
    </section>
  );
}

function AboutSummary() {
  const pts = [
    { icon: Gavel, t: "Post once, compare bids", b: "Upload specs, dielines and artwork once. Verified regional printers compete in a sealed reverse auction, saving buyers 18–34% on average." },
    { icon: CheckCircle2, t: "Automated pre-flight check", b: "Artwork is audited instantly for CMYK colour, 300+ DPI resolution and vector cut-lines before production begins." },
    { icon: ShieldCheck, t: "100% secure escrow", b: "No blind advance payments. Funds stay locked in escrow and reach the printer only after delivery is verified." },
  ];
  return (
    <section className="m-about-sum" data-testid="home-about-summary">
      <div className="m-section-head">
        <p className="eyebrow">ONE DIGITAL HUB</p>
        <h2>The entire print &amp; packaging market on a single platform.</h2>
        <p className="m-lede">No more calling or emailing dozens of factories and waiting days for inconsistent quotes.</p>
      </div>
      <div className="m-about-grid">
        {pts.map((p, i) => (
          <div key={p.t} className="m-about-card" data-testid={`home-about-point-${i}`}>
            <div className="m-value-icon"><p.icon size={19}/></div>
            <h3>{p.t}</h3>
            <p>{p.b}</p>
          </div>
        ))}
      </div>
      <Link to="/about" className="m-outline" data-testid="home-about-link">Read more about GoPrintPack <ArrowRight size={15}/></Link>
    </section>
  );
}

function ValueProps() {
  const items = [
    { icon: TrendingDown, title: "18–34% average savings", body: "Regional suppliers with idle press capacity compete privately for your run — no more chasing 6 quotes." },
    { icon: Lock, title: "Anonymous Bids", body: "Buyers see only radius, rating, turnaround, and a masked score. Prevents lowball collusion." },
    { icon: ShieldCheck, title: "Funds in escrow", body: "Razorpay escrow holds payment until the courier confirms delivery. Full refund on non-performance." },
    { icon: Sparkles, title: "Design agencies on-demand", body: "No press-ready art? Book vetted packaging designers with a structured brief and 3D soft-proof." },
  ];
  return (
    <section className="m-values" data-testid="value-props">
      <div className="m-section-head">
        <p className="eyebrow">WHY BUYERS SWITCH</p>
        <h2>Competitive Bids. Unmatched Quality. Zero Friction.</h2>
      </div>
      <div className="m-value-grid">
        {items.map((it, i) => (
          <div key={it.title} className="m-value-card" data-testid={`value-card-${i}`}>
            <div className="m-value-icon"><it.icon size={19}/></div>
            <h3>{it.title}</h3>
            <p>{it.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Layers,      t: "1 · Post",     b: "Spec your request — print, materials, finishing or procurement." },
    { icon: Target,      t: "2 · Match",    b: "We route it to capability-matched verified suppliers only." },
    { icon: Gavel,       t: "3 · Compete",  b: "Sealed anonymous bids under full escrow protection." },
    { icon: Truck,       t: "4 · Deliver",  b: "Blind dispatch — delivery scan auto-releases your payout." },
  ];
  return (
    <section className="m-how" id="how" data-testid="how-it-works">
      <div className="m-section-head">
        <p className="eyebrow">HOW IT WORKS</p>
        <h2>Four steps. One platform. Every print &amp; packaging need.</h2>
      </div>
      <div className="m-step-grid m-step-grid-4">
        {steps.map((s, i) => (
          <div key={s.t} className="m-step m-step-marketing" data-testid={`step-${i+1}`}>
            <div className="m-step-icon"><s.icon size={20}/></div>
            <h3>{s.t}</h3>
            <p>{s.b}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function BuyerProtection() {
  return (
    <section className="m-protection" id="protection" data-testid="buyer-protection">
      <div className="m-protection-copy">
        <p className="eyebrow">BUYER PROTECTION</p>
        <h2>Your funds are safe. Always.</h2>
        <p>Every award captures payment into a Razorpay escrow. Funds are held on the platform account and only transferred to the supplier after (a) tracking dispatch is verified, (b) a 48-hour clearance window elapses, and (c) delivery is confirmed. If a supplier fails to perform, funds are refunded in full — automatically.</p>
        <ul>
          <li data-testid="protection-1"><CheckCircle2 size={16}/> Anonymous bidding prevents price collusion.</li>
          <li data-testid="protection-2"><CheckCircle2 size={16}/> Admin-set price floors reject unrealistic lowballs.</li>
          <li data-testid="protection-3"><CheckCircle2 size={16}/> Verified network — 3-point supplier vetting, insurance on file.</li>
          <li data-testid="protection-4"><CheckCircle2 size={16}/> 100% refund if delivery isn't confirmed within 30 days.</li>
        </ul>
      </div>
      <div className="m-protection-visual">
        <div className="m-escrow-card">
          <div className="m-escrow-head">
            <ShieldCheck size={20}/>
            <div><b>₹2,28,500.00 held in escrow</b><span>Order · Summer coffee pouch run</span></div>
          </div>
          <div className="m-escrow-timeline">
            <TimelineItem done label="Auction awarded" note="14 Feb, 10:02"/>
            <TimelineItem done label="Escrow captured" note="14 Feb, 10:02"/>
            <TimelineItem done label="In production" note="17 Feb"/>
            <TimelineItem label="Dispatched" note="Pending"/>
            <TimelineItem label="Delivery confirmed → payout" note="Automatic"/>
          </div>
        </div>
      </div>
    </section>
  );
}

const TimelineItem = ({ done, label, note }) => (
  <div className={done ? "m-tl-item done" : "m-tl-item"}>
    <div className="m-tl-dot"/>
    <div><b>{label}</b><span>{note}</span></div>
  </div>
);

function ThreeSided() {
  return (
    <section className="m-network" id="network" data-testid="three-sided">
      <div className="m-section-head">
        <p className="eyebrow">FIVE-SIDED MARKETPLACE</p>
        <h2>One platform. Five sides of the print &amp; packaging stack.</h2>
        <p className="m-lede">Buyers post sealed RFQs. Printers bid anonymously. Agencies deliver press-ready art. Material suppliers quote raw stock on sub-RFQs. Finishing partners slot in on specific jobs. Every rupee stays in escrow until delivery.</p>
      </div>
      <div className="m-network-grid m-network-grid-5">
        <NetCard
          title="Buyers"
          line="Brand operators & procurement leads"
          points={["Auto-matched to capable suppliers","Anonymous sealed bidding","Escrow-protected payment"]}
          testid="netcard-buyer"
        />
        <NetCard
          title="Printers &amp; Packagers"
          line="Regional presses with idle capacity"
          points={["Capability-matched job feed","One private sealed bid — no race to the bottom","Fast payouts on delivery"]}
          testid="netcard-printer"
          primary
        />
        <NetCard
          title="Design Agencies"
          line="Vetted packaging & label creatives"
          points={["Briefs pre-qualified by the platform","3D soft-proof handoff","Design escrow released on approval"]}
          testid="netcard-agency"
        />
        <NetCard
          title="Material Suppliers"
          line="Paper, boards, films, inks, plates, adhesives"
          points={["Sealed RFQs from buyers","Order-level demand — not vague leads","Grow with every press we onboard"]}
          testid="netcard-material"
          badge="NEW"
          accent="#10B981"
        />
        <NetCard
          title="Finishing Partners"
          line="Die-cut, lamination, binding, last-mile"
          points={["Slot in on specific orders","SLA-matched by service type","Pay-per-use, no listing fees"]}
          testid="netcard-finishing"
          badge="NEW"
          accent="#F59E0B"
        />
      </div>
    </section>
  );
}

const NetCard = ({ title, line, points, primary, testid, badge, accent }) => (
  <div
    className={`m-net-card${primary ? " primary" : ""}${accent ? " accented" : ""}`}
    data-testid={testid}
    style={accent ? { "--netcard-accent": accent } : undefined}
  >
    {badge && <span className="m-net-badge" style={accent ? { background: accent } : undefined}>{badge}</span>}
    <h3 dangerouslySetInnerHTML={{ __html: title }} />
    <span>{line}</span>
    <ul>{points.map(p => <li key={p}><CheckCircle2 size={14}/> {p}</li>)}</ul>
  </div>
);

function Numbers() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/stats`).then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);
  const items = [
    { v: "24%", l: "Avg. saved vs. direct sourcing" },
    { v: "4.7h", l: "Median time-to-first-bid" },
    { v: stats ? `${stats.verified_suppliers_total}+` : "…", l: "Verified regional suppliers", testid: "stat-verified" },
    { v: stats ? inrCompact(stats.escrow_processed_inr) : "₹0", l: "Escrow processed year-to-date", testid: "stat-escrow" },
  ];
  return (
    <section className="m-numbers" data-testid="numbers">
      {items.map(s =>
        <div key={s.l} className="m-num" data-testid={s.testid}>
          <strong>{s.v}</strong><span>{s.l}</span>
        </div>
      )}
    </section>
  );
}

function LiveSupplierBadge() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/stats`).then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);
  if (!stats || stats.verified_suppliers_total < 1) return null;
  return (
    <div className="live-badge" data-testid="live-supplier-badge">
      <span className="live-dot"/>
      <b>{stats.verified_suppliers_total}</b> verified {stats.verified_suppliers_total === 1 ? "supplier" : "suppliers"} online
      {stats.active_auctions > 0 && <span className="live-sep">·</span>}
      {stats.active_auctions > 0 && <><b>{stats.active_auctions}</b> live auction{stats.active_auctions !== 1 ? "s" : ""}</>}
    </div>
  );
}

function FinalCta() {
  return (
    <section className="m-final" data-testid="final-cta">
      <p className="eyebrow">READY WHEN YOU ARE</p>
      <h2>Post a spec. Watch verified suppliers compete privately.</h2>
      <p>Free to list. Only pay when you award a winning bid.</p>
      <Link to="/register" className="m-cta m-cta-lg" data-testid="final-cta-button">
        Launch the workspace <ArrowRight size={17}/>
      </Link>
    </section>
  );
}

function Footer() {
  return (
    <footer className="m-footer" data-testid="marketing-footer">
      <div>
        <div className="m-brand">
          <img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/>
        </div>
        <p className="m-foot-copy">Asset-light procurement infrastructure for brand operators, regional printers, and packaging design agencies. A smart platform for print & packaging.</p>
      </div>
      <div className="m-foot-links">
        <div>
          <b>Product</b>
          <a href="#how">How it works</a>
          <a href="#protection">Buyer protection</a>
          <a href="#network">Supplier network</a>
        </div>
        <div>
          <b>Company</b>
          <Link to="/about" data-testid="footer-about">About us</Link>
          <Link to="/careers" data-testid="footer-careers">Careers</Link>
          <Link to="/contact" data-testid="footer-contact">Contact us</Link>
          <Link to="/partner" data-testid="footer-partner">Partner with us</Link>
        </div>
        <div>
          <b>Legal</b>
          <Link to="/terms"           data-testid="footer-terms">Terms of Service</Link>
          <Link to="/privacy"         data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/escrow-policy"   data-testid="footer-escrow">Escrow Policy</Link>
          <Link to="/refund-policy"   data-testid="footer-refund">Refund & Cancellation</Link>
          <Link to="/dispute-policy"  data-testid="footer-dispute">Dispute Resolution</Link>
          <Link to="/shipping-policy" data-testid="footer-shipping">Shipping & Delivery</Link>
          <Link to="/acceptable-use"  data-testid="footer-aup">Acceptable Use & NDA</Link>
          <Link to="/cookie-policy"   data-testid="footer-cookies">Cookie Policy</Link>
        </div>
      </div>
    </footer>
  );
}
